import { useCallback } from "react";
import {
  PayableImportBatchItemData,
  PayableImportBatchItemDataLineItemType,
  PayeeEngagement,
  PayeeEngagementStatus,
  PayeeRow
} from "@wingspanhq/wingspan-api-sdk";
import { batchesApi, payeeEngagementsApi, payeeRowsApi } from "@api";

import { pMap } from "../../../../utils/pMap";
import { getApiErrorMessage, isApiError } from "../../../../v3/utils/apiError";

export type PayableImportRow = Record<string, unknown>;

export type PayableImportUploadResult = {
  submitted: number;
  rejected: Array<{ rowIndex: number; message: string }>;
};

const PAYEE_PAGE_SIZE = 100;
/** Caps the walk at 50k payees so a repeating cursor cannot hang the browser. */
const MAX_PAYEE_PAGES = 500;

/**
 * v3 requires a `payeeEngagementId` on every imported line item, but the
 * spreadsheet contract identifies contractors by email or external ID — the v1
 * backend resolved that server-side.
 *
 * This takes two passes rather than one: `searchPayeeRows` carries the email
 * but never populates `engagements` or `externalId` (both are in the SDK model
 * and absent from every row the API returns — ARC-1062), so the engagement id
 * has to come from `listPayeeEngagementsByPayer` and be joined back on
 * `payeeId`. Both are whole-collection reads done once per upload, not per row.
 * Drop the second pass once ARC-1062 populates the row.
 */
const buildPayeeEngagementIndex = async () => {
  const byEmail = new Map<string, string>();
  const byExternalId = new Map<string, string>();
  const engagementByPayeeId = new Map<string, PayeeEngagement>();

  let pageToken: string | undefined;
  let pagesFetched = 0;

  do {
    const page = await payeeEngagementsApi.listPayeeEngagementsByPayer({
      page: {
        size: PAYEE_PAGE_SIZE,
        ...(pageToken ? { token: pageToken } : {})
      }
    });
    pagesFetched += 1;

    (page.data ?? []).forEach((engagement: PayeeEngagement) => {
      if (
        !engagement.id ||
        engagement.status !== PayeeEngagementStatus.ACTIVATED
      ) {
        return;
      }
      // A payee can hold several engagements; the default is the one v1's
      // importer implicitly used when the sheet named none.
      const held = engagement.payeeId
        ? engagementByPayeeId.get(engagement.payeeId)
        : undefined;
      if (engagement.payeeId && (!held || engagement.isDefault)) {
        engagementByPayeeId.set(engagement.payeeId, engagement);
      }
      if (engagement.externalId) {
        byExternalId.set(engagement.externalId.trim(), engagement.id);
      }
    });

    pageToken = page.pagination?.nextPageToken;
  } while (pageToken && pagesFetched < MAX_PAYEE_PAGES);

  pageToken = undefined;
  pagesFetched = 0;

  do {
    const page = await payeeRowsApi.searchPayeeRows({
      page: {
        size: PAYEE_PAGE_SIZE,
        ...(pageToken ? { token: pageToken } : {})
      }
    });
    pagesFetched += 1;

    (page.data ?? []).forEach((payeeRow: PayeeRow) => {
      const engagement = payeeRow.id
        ? engagementByPayeeId.get(payeeRow.id)
        : undefined;

      if (!engagement?.id) {
        return;
      }
      if (payeeRow.email) {
        byEmail.set(payeeRow.email.trim().toLowerCase(), engagement.id);
      }
      if (payeeRow.externalId) {
        byExternalId.set(payeeRow.externalId.trim(), engagement.id);
      }
    });

    pageToken = page.pagination?.nextPageToken;
  } while (pageToken && pagesFetched < MAX_PAYEE_PAGES);

  return { byEmail, byExternalId };
};

const toNumber = (value: unknown) =>
  typeof value === "number"
    ? value
    : Number(String(value ?? "").replace(/,/g, ""));

const ISO_DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Flatfile hands back a `date` cell as `YYYY-MM-DD`. `new Date("2026-03-15")`
 * is parsed as UTC midnight, which serializes back one day earlier for every
 * payer west of UTC — so a date-only value is built in local time instead.
 */
const toDate = (value: unknown): Date | undefined => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : value;
  }

  const raw = String(value ?? "").trim();
  if (!raw) {
    return undefined;
  }

  if (ISO_DATE_ONLY.test(raw)) {
    const [year, month, day] = raw.split("-").map(Number);
    return new Date(year, month - 1, day);
  }

  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
};

const isTruthyCell = (value: unknown) =>
  value === true || /^(true|yes|y|1)$/i.test(String(value ?? "").trim());

/** FNV-1a, so the reference key stays short and free of row content. */
const hashRow = (parts: Array<string | number | undefined>) => {
  let hash = 0x811c9dc5;
  const input = parts.map(part => String(part ?? "")).join(" ");

  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }

  return hash.toString(16).padStart(8, "0");
};

export const usePayableImportUpload = (batchId: string) =>
  useCallback(
    async (rows: PayableImportRow[]): Promise<PayableImportUploadResult> => {
      const index = await buildPayeeEngagementIndex();
      const rejected: PayableImportUploadResult["rejected"] = [];
      // Two identical lines in one sheet are legitimate, so a content hash
      // alone would merge them into one. The occurrence counter keeps them
      // distinct while staying stable across re-uploads of the same sheet.
      const referenceKeyUses = new Map<string, number>();

      const items = rows.flatMap((row, rowIndex) => {
        const email = String(row.email ?? "")
          .trim()
          .toLowerCase();
        const externalId = String(row.contractorId ?? "").trim();
        const payeeEngagementId =
          (email && index.byEmail.get(email)) ||
          (externalId && index.byExternalId.get(externalId));

        if (!payeeEngagementId) {
          rejected.push({
            rowIndex,
            message:
              "No contractor on this account matches the email or external ID in this row."
          });
          return [];
        }

        const dueDate = toDate(row.dueDate);
        if (!dueDate) {
          rejected.push({
            rowIndex,
            message: "Due date is missing or is not a date we can read."
          });
          return [];
        }

        const totalCost = toNumber(row.amount);
        if (!Number.isFinite(totalCost)) {
          rejected.push({
            rowIndex,
            message: "Amount is not a number."
          });
          return [];
        }

        const description = String(row.lineItemTitle ?? "");
        const providedReference = String(row.paymentRefId ?? "").trim();

        // v3 makes the reference key mandatory — it is what a repeat import
        // matches an existing line item on. Rows carrying no reference of their
        // own get one derived from their content, never from their position:
        // a positional key restarts at 0 on the next upload into the same
        // batch and would merge unrelated rows onto each other.
        let uniqueReferenceKey = providedReference;
        if (!uniqueReferenceKey) {
          const base = hashRow([
            payeeEngagementId,
            dueDate.toISOString().slice(0, 10),
            description,
            totalCost
          ]);
          const seen = referenceKeyUses.get(base) ?? 0;
          referenceKeyUses.set(base, seen + 1);
          uniqueReferenceKey = seen === 0 ? base : `${base}-${seen}`;
        }

        const data: PayableImportBatchItemData = {
          payeeEngagementId,
          uniqueReferenceKey,
          totalCost,
          description,
          detail: String(row.lineItemDescription ?? ""),
          dueDate,
          ...(toDate(row.paidDate) ? { paidDate: toDate(row.paidDate) } : {}),
          ...(row.invoiceNotes ? { notes: String(row.invoiceNotes) } : {}),
          ...(isTruthyCell(row.reimbursable)
            ? {
                lineItemType:
                  PayableImportBatchItemDataLineItemType.REIMBURSEMENT
              }
            : {}),
          ...(row.attachmentId
            ? { attachmentFileId: String(row.attachmentId) }
            : {})
        };

        return [{ rowIndex, data }];
      });

      const { errors } = await pMap(
        items,
        (item: { rowIndex: number; data: PayableImportBatchItemData }) =>
          // A replay of the same key returns the original item rather than
          // creating a second one, so a retry after a client-side timeout
          // cannot duplicate the row.
          batchesApi.addBatchItem({
            batchId,
            batchItemCreate: { data: item.data },
            idempotencyKey: `${batchId}:${item.data.uniqueReferenceKey}`
          }),
        {
          concurrency: 25,
          retryConfig: {
            retries: 3,
            delay: 100,
            backoff: "EXPONENTIAL",
            // pMap's own predicate only understands axios errors, and the v3
            // SDK throws fetch-based `ApiError`s — without this every rejected
            // row would be re-POSTed three more times.
            retryIf: (error: unknown) =>
              !isApiError(error) || error.status >= 500
          }
        }
      );

      errors.forEach(({ item, error }) => {
        rejected.push({
          rowIndex: (item as { rowIndex: number }).rowIndex,
          message:
            getApiErrorMessage(error) ??
            (error as Error)?.message ??
            "Failed to import this row."
        });
      });

      return { submitted: items.length - errors.length, rejected };
    },
    [batchId]
  );
